import { AlertTriangle, CheckCircle2, CircleDot, Layers } from "lucide-react";

import type { Verdict } from "@talent-align/engine";

interface Props {
  score: number;
  verdict: Verdict;
  /** Day 9: cross-encoder score in [0, 1] if the reranker ran on this match. */
  crossEncoderScore?: number;
  /** Day 9: pre-rerank bi-encoder score for delta display. */
  biencoderScore?: number;
}

const CONFIG = {
  strong: {
    label: "Strong fit",
    Icon: CheckCircle2,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
  },
  partial: {
    label: "Partial fit",
    Icon: CircleDot,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
  },
  weak: {
    label: "Weak fit",
    Icon: AlertTriangle,
    color: "text-rose-400",
    bg: "bg-rose-500/10",
    border: "border-rose-500/30",
  },
} as const;

export function ScoreBadge({
  score,
  verdict,
  crossEncoderScore,
  biencoderScore,
}: Props) {
  const cfg = CONFIG[verdict];
  const hasRerank =
    crossEncoderScore !== undefined && biencoderScore !== undefined;

  return (
    <div
      className={`ta-card p-6 flex items-center justify-between border ${cfg.border}`}
    >
      <div>
        <div
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs uppercase tracking-wider font-semibold ${cfg.bg} ${cfg.color} ${cfg.border} border`}
        >
          <cfg.Icon className="w-3.5 h-3.5" />
          {cfg.label}
        </div>
        <div className="mt-3 text-sm text-slate-400">
          Overall match score
        </div>

        {hasRerank && (
          <div className="mt-3 pt-3 border-t border-slate-800/60 max-w-md">
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-blue-300 font-semibold mb-2">
              <Layers className="w-3 h-3" />
              Two-stage retrieval
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-slate-500">Bi-encoder (BGE)</div>
                <div className="text-slate-300 font-mono font-semibold">
                  {(biencoderScore ?? 0).toFixed(1)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Cross-encoder rerank</div>
                <div className="text-slate-300 font-mono font-semibold">
                  {((crossEncoderScore ?? 0) * 100).toFixed(1)}
                </div>
              </div>
            </div>
            <div className="text-[10px] text-slate-600 mt-2 leading-relaxed">
              Final score blends bi-encoder (60%) + cross-encoder (40%). The
              cross-encoder processes both documents jointly through a
              transformer for more accurate pairwise relevance.
            </div>
          </div>
        )}
      </div>
      <div className="text-right">
        <div className={`text-6xl font-extrabold ${cfg.color} leading-none`}>
          {Math.round(score)}
        </div>
        <div className="text-xs text-slate-500 mt-1">/ 100</div>
      </div>
    </div>
  );
}
